import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/quienes-somos', label: 'Quiénes Somos' },
    { path: '/servicios', label: 'Servicios' },
    // «DeCA» a secas no lo entiende quien no sea del sector.
    { path: '/deca', label: 'DeCA Transporte' },
    { path: '/clientes', label: 'Clientes' },
    { path: '/contacto', label: 'Contacto' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg' 
        : 'bg-transparent'
    }`}>
      {/* Top bar with contact info */}
      <div className="bg-slate-900 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center text-sm">
          {/* Los números se ven también en el móvil: antes se escondían y
              quedaban el icono y una «/» suelta, justo en la pantalla desde la
              que más se llama. El correo sí se queda en icono, no cabe. */}
          <div className="flex items-center gap-3 sm:gap-4">
            <a href="tel:945600676" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              <Phone className="w-4 h-4" />
              <span>945 600 676</span>
            </a>
            <span aria-hidden="true">/</span>
            <a href="tel:629125142" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              <span>629 125 142</span>
            </a>
            <a href="mailto:info@ayg-asesores.com" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              <Mail className="w-4 h-4" />
              <span className="hidden md:inline">info@ayg-asesores.com</span>
            </a>
          </div>
          <div className="text-xs hidden lg:block">
            Calle Mayor 55, 1º, 01300 Laguardia, Álava
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-4 py-4 relative">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/logo.png" 
              alt="Argomaniz y Garcia Asesores" 
              className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
            />
            <div className={`transition-colors duration-300 ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
              <div className="font-bold text-lg leading-tight">Argomaniz y Garcia</div>
              <div className="text-xs opacity-80">Asesores</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  location.pathname === link.path
                    ? isScrolled 
                      ? 'bg-slate-900 text-white' 
                      : 'bg-white/20 backdrop-blur-sm text-white'
                    : isScrolled
                      ? 'text-slate-700 hover:bg-slate-100'
                      : 'text-white hover:bg-white/10 backdrop-blur-sm'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-slate-900' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 mt-2 mx-4 py-4 bg-white rounded-xl shadow-2xl z-50">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-6 py-3 font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
};
